import React, { useEffect } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Input } from '@availity/form';
import { FormGroup, Label } from 'reactstrap';
import StateSelectField from '../../../../../components/FormFields/StateSelectField/StateSelectField';
import './MeetingLocationEditForm.scss';

const validationSchema = yup.object().shape({
    city: yup
        .string()
        .trim()
        .required('City is required'),
    state: yup
        .string()
        .trim()
        .required('State is required'),
    address: yup.string().nullable(),
});

export interface MeetingLocationEditFormValues {
    city: string;
    state: string;
    address: string;
}

const ValuesChangeReporter: React.FC<{
    values: MeetingLocationEditFormValues;
    onValuesChange?: (values: MeetingLocationEditFormValues) => void;
}> = ({ values, onValuesChange }) => {
    useEffect(() => {
        onValuesChange?.(values);
    }, [values.city, values.state, values.address, onValuesChange]);
    return null;
};

export interface MeetingLocationEditFormProps {
    initialValues: MeetingLocationEditFormValues;
    onSubmit: (values: MeetingLocationEditFormValues) => Promise<void>;
    onCancel: () => void;
    isSaving?: boolean;
    /** Called when form is ready so parent can trigger submit (e.g. from Save icon) */
    onFormReady?: (api: { submitForm: () => void }) => void;
    /** Called when form values change so parent can enable/disable Save (e.g. when values differ from initial) */
    onValuesChange?: (values: MeetingLocationEditFormValues) => void;
}

export const MeetingLocationEditForm: React.FC<MeetingLocationEditFormProps> = ({
    initialValues,
    onSubmit,
    onCancel,
    isSaving = false,
    onFormReady,
    onValuesChange,
}) => {
    return (
        <Formik<MeetingLocationEditFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
                await onSubmit(values);
            }}
            enableReinitialize>
            {({
                values,
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                handleSubmit,
                submitForm,
            }) => {
                if (onFormReady) {
                    onFormReady({ submitForm });
                }
                return (
                <>
                <ValuesChangeReporter values={values} onValuesChange={onValuesChange} />
                <form
                    className="meeting-location-edit-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}>
                    <FormGroup className="editable-address-form-field">
                        <Label className="editable-address-field-label">
                            City
                        </Label>
                        <Input
                            type="text"
                            name="city"
                            value={values.city || ''}
                            onChange={(e) =>
                                setFieldValue('city', e.target.value)
                            }
                            onBlur={() => setFieldTouched('city', true)}
                            disabled={isSaving}
                            placeholder="City"
                            className="form-control form-control-sm"
                            invalid={!!(touched.city && errors.city)}
                        />
                        {touched.city && errors.city && (
                            <div className="invalid-feedback d-block">
                                {errors.city}
                            </div>
                        )}
                    </FormGroup>
                    <FormGroup className="editable-address-form-field">
                        <StateSelectField
                            name="state"
                            value={values.state}
                            onChange={(e) => {
                                setFieldValue('state', e.target.value);
                                setFieldTouched('state', true);
                            }}
                            disabled={isSaving}
                            label="State"
                            placeholder="--"
                            className="editable-address-state-select"
                        />
                        {touched.state && errors.state && (
                            <div className="invalid-feedback d-block">
                                {errors.state}
                            </div>
                        )}
                    </FormGroup>
                    <FormGroup className="editable-address-form-field">
                        <Label className="editable-address-field-label">
                            Address
                        </Label>
                        <Input
                            type="text"
                            name="address"
                            value={values.address || ''}
                            onChange={(e) =>
                                setFieldValue('address', e.target.value)
                            }
                            onBlur={() => setFieldTouched('address', true)}
                            disabled={isSaving}
                            placeholder="Address (optional)"
                            className="form-control form-control-sm"
                        />
                    </FormGroup>
                </form>
                </>
                );
            }}
        </Formik>
    );
};
